import React, { useRef } from 'react'
import { ProgressBar as PrimeProgressBar } from 'primereact/progressbar'

import { layoutIsCompact } from '../../lib/layout'

import useViewport from '../../modules/viewport/useViewport'
import useSurveyLocale from '../../modules/locale/useSurveyLocale'
import useSession from '../useSession'

import style from './style.module.scss'

export interface IProgressBarProps {
  total: number
  current: number
  backgroundColor?: string
  isRtl?: boolean
}

const ProgressBar = React.memo<IProgressBarProps>(
  ({ total, current, backgroundColor, isRtl }) => {
    const viewport = useViewport()
    const session = useSession()
    const { t } = useSurveyLocale()

    const containerStyleRef = useRef<{ ['--prime-progressbar-bg']: string }>()
    if (
      backgroundColor &&
      (!containerStyleRef.current || containerStyleRef.current['--prime-progressbar-bg'] !== backgroundColor)
    ) {
      containerStyleRef.current = { ['--prime-progressbar-bg']: backgroundColor }
    } else if (!backgroundColor && containerStyleRef.current) {
      containerStyleRef.current = void 0
    }

    return (
      <div
        className={`
          ${style.ProgressBar} ${viewport.mobile ? style.mobile : ''} ${isRtl ? style.ProgressBarRTL : ''}${
            layoutIsCompact(session.props.layout) ? style.compact : ''
          }`}
        style={containerStyleRef.current as React.CSSProperties}
      >
        {0 === total ? (
          <PrimeProgressBar
            mode="indeterminate"
            aria-label={t('navigation', 'progressBar', 'label')}
            data-testid="SW-Progress"
          />
        ) : (
          <PrimeProgressBar
            showValue={false}
            value={Math.max(1, Math.min(100, 100 * (current / total)))}
            aria-label={t('navigation', 'progressBar', 'label')}
            data-testid="SW-Progress"
          />
        )}
      </div>
    )
  },
  (prevProps, nextProps) => prevProps.total === nextProps.total && prevProps.current === nextProps.current
)

ProgressBar.displayName = 'ProgressBar'

export default ProgressBar
